import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Modal, ModalDescription, ModalTitle } from "@air/primitive-modal";
import { RadioGroup, RadioGroupItem } from "@air/primitive-radio-group";
import { useAirModal } from "@air/provider-modal";
import { useToasts } from "@air/provider-toast";
import Dinero from "dinero.js";
import Link from "next/link";
import { useState } from "react";
import { Routes } from "~/constants/routes";
import { UPGRADE_CONFIRMATION_MODAL, UPGRADE_CONFIRMATION_MODAL_BUTTON } from "~/constants/testIDs";
import { useBillingCycle } from "~/hooks/useBillingCycle";
import { useCurrentWorkspace } from "~/providers/CurrentWorkspaceProvider";
import { useUpdateSubscription } from "~/swr-hooks/subscriptions/useUpdateSubscription";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { PlansModal } from "./PlansModal/PlansModal";
/**
 * This component (used as a modal) displays confirmation for plan upgrade. If a user confirms,
 * it calls updateSubscription method with selected planId, fetches new subscription, closes dialog and shows a toast
 */ const UpgradePlanConfirmationModal = (param)=>{
    let { priceDetail, prices, stripePlanIds, onClose } = param;
    const { showToast } = useToasts();
    const [isLoading, setIsLoading] = useState(false);
    const [_showPlansModal, closePlansModal] = useAirModal(PlansModal);
    const { currentWorkspace } = useCurrentWorkspace();
    const { updateSubscription } = useUpdateSubscription();
    const [billingCycle, setBillingCycle] = useBillingCycle();
    const monthlyFormattedPrice = prices["monthly"] ? Dinero({
        amount: prices["monthly"]
    }).toFormat(prices["monthly"] % 100 ? "$0,0.00" : "$0,0") : "Free";
    const annualFormattedPrice = prices["annual"] ? Dinero({
        amount: prices["annual"]
    }).toFormat(prices["annual"] % 100 ? "$0,0.00" : "$0,0") : "Free";
    const stripePlanId = stripePlanIds[billingCycle];
    const purchasePlan = async ()=>{
        try {
            const workspaceId = currentWorkspace === null || currentWorkspace === void 0 ? void 0 : currentWorkspace.id;
            if (!workspaceId) {
                throw new Error("No workspace id");
            }
            setIsLoading(true);
            if (stripePlanId) {
                await updateSubscription({
                    workspaceId,
                    planId: stripePlanId
                });
            }
            setIsLoading(false);
            closePlansModal();
            onClose();
            showToast("Your purchase was successful");
        } catch (error) {
            reportErrorToBugsnag({
                error,
                context: "Failed to upgrade plan",
                metadata: {
                    data: {
                        stripePlanId
                    }
                }
            });
            setIsLoading(false);
        }
    };
    return /*#__PURE__*/ _jsxs(Modal, {
        dangerouslyBypassFocusLock: true,
        "data-testid": UPGRADE_CONFIRMATION_MODAL,
        isOpen: true,
        onDismiss: onClose,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "mb-4 flex flex-col gap-2",
                children: [
                    /*#__PURE__*/ _jsx(ModalTitle, {
                        children: "Upgrade now?"
                    }),
                    /*#__PURE__*/ _jsx(ModalDescription, {
                        children: "Your next invoice will be automatically prorated to reflect your workspace's new subscription."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mb-2 text-16 font-semibold text-grey-12",
                children: "Select your billing cycle"
            }),
            /*#__PURE__*/ _jsxs(RadioGroup, {
                value: billingCycle,
                onValueChange: (value)=>setBillingCycle(value),
                children: [
                    /*#__PURE__*/ _jsxs(RadioGroupItem, {
                        align: "start",
                        id: "monthly",
                        value: "monthly",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-16 font-medium text-grey-12",
                                children: "Monthly"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-14 font-normal text-grey-10",
                                children: [
                                    monthlyFormattedPrice,
                                    priceDetail ? " ".concat(priceDetail, ",") : "",
                                    " billed monthly"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(RadioGroupItem, {
                        align: "start",
                        id: "annual",
                        value: "annual",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-16 font-medium text-grey-12",
                                children: "Annual - Save 17%"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-14 font-normal text-grey-10",
                                children: [
                                    annualFormattedPrice,
                                    priceDetail ? " ".concat(priceDetail, ", ") : "",
                                    "billed annually"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-4 text-14 text-grey-11",
                children: [
                    "By confirming your subscription, you allow Air to charge your card for this payment and future payments in accordance with their",
                    " ",
                    /*#__PURE__*/ _jsx(Link, {
                        href: Routes.marketing.terms,
                        target: "_blank",
                        children: "terms"
                    }),
                    "."
                ]
            }),
            /*#__PURE__*/ _jsxs("footer", {
                className: "mt-8 flex justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "ghost",
                        color: "grey",
                        onClick: onClose,
                        size: "large",
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        "data-testid": UPGRADE_CONFIRMATION_MODAL_BUTTON,
                        appearance: "filled",
                        color: "blue",
                        isLoading: isLoading,
                        onClick: purchasePlan,
                        size: "large",
                        children: "Upgrade"
                    })
                ]
            })
        ]
    });
};
export default UpgradePlanConfirmationModal;
